// src/landing/DemoLeadForm.jsx
// ─── Formulaire de démonstration (§22-39) ───────────────────────────────────
// Utilisé par le hero de la landing ET par la page /demo. Un seul composant,
// un seul endpoint (/api/public/demo-lead), une seule table CRM (pro_leads).
//
// §30-33 — Attribution. Les paramètres d'URL sont lus au montage et joints au
// lead : utm_* pour les campagnes, ref pour un apporteur. Ils sont conservés
// en sessionStorage : un visiteur arrivé par une publicité, qui navigue trois
// pages puis remplit le formulaire, garde sa provenance.
//
// §38-39 — Tracking. Quatre évènements sont poussés vers dataLayer (GA) et
// fbq (Meta) s'ils existent. JAMAIS le nom, l'email ou le téléphone : seuls
// des drapeaux et la source.

import { useState, useEffect, useRef } from "react";
import InternationalPhoneField from "../components/InternationalPhoneField";

const NEEDS = [
  ["gain_temps", "Gagner du temps et réduire les ressaisies"], ["analyse_vehicules", "Fiabiliser mes décisions véhicule"],
  ["opportunites", "Optimiser mon sourcing"], ["structurer_dossiers", "Centraliser mes flux d’information"],
  ["piloter_equipe", "Piloter mes objectifs et mon équipe"], ["autre", "Autre"],
];
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

const COMPANY_SIZES = [
  ["1", "1 personne"],
  ["2_3", "2 à 3 personnes"],
  ["4_9", "4 à 9 personnes"],
  ["10_19", "10 à 19 personnes"],
  ["20_49", "20 à 49 personnes"],
  ["50_plus", "50 personnes et plus"],
];

// Les seuils suivent volontairement les zones de prix annuelles des offres
// VehicleIQ (≈ 2 999 € / 4 999 €) afin de qualifier le budget sans demander
// un montant trop précis au prospect.
const DIGITAL_BUDGETS = [
  ["lt_1500", "Moins de 1 500 € / an"],
  ["1500_2999", "1 500 à 2 999 € / an"],
  ["3000_4999", "3 000 à 4 999 € / an"],
  ["5000_7499", "5 000 à 7 499 € / an"],
  ["7500_9999", "7 500 à 9 999 € / an"],
  ["10000_plus", "10 000 € et plus / an"],
];

const BUSINESS_TOOLS = [
  ["dms", "DMS / logiciel VO"],
  ["crm", "CRM"],
  ["diffusion", "Outil de diffusion d'annonces"],
  ["sourcing", "Outil de sourcing"],
  ["compta", "Comptabilité / facturation"],
  ["agenda", "Agenda / calendrier"],
  ["aucun", "Aucun outil métier"],
  ["autre", "Autre"],
];

export function captureAttribution() {
  if (typeof window === "undefined") return {};
  try {
    const stored = JSON.parse(sessionStorage.getItem("viq_attribution") || "{}");
    const q = new URLSearchParams(window.location.search);
    const fresh = {};
    for (const k of UTM_KEYS) { const v = q.get(k); if (v) fresh[k] = v.slice(0, 120); }
    const ref = q.get("ref") || q.get("lead");
    if (ref) fresh.ref = ref.toUpperCase().slice(0, 40);
    const merged = { ...stored, ...fresh };
    if (!merged.landing_page) merged.landing_page = window.location.pathname + window.location.search;
    if (!merged.referrer && document.referrer) merged.referrer = document.referrer.slice(0, 300);
    sessionStorage.setItem("viq_attribution", JSON.stringify(merged));
    return merged;
  } catch (e) { return {}; }
}

function track(event, data = {}) {
  try {
    if (window.dataLayer) window.dataLayer.push({ event, ...data });
    if (typeof window.fbq === "function") {
      if (event === "lead_form_success") window.fbq("track", "Lead", data);
      else window.fbq("trackCustom", event, data);
    }
  } catch (e) {}
}

export default function DemoLeadForm({ apiUrl, onPrivacy, onDiscover, variant = "hero" }) {
  const [f, setF] = useState({
    name: "", company: "", siret: "", address: "", company_size: "", annual_digital_budget: "",
    email: "", phone: "", vehicle_volume: "", primary_need: "", business_tools: [], website: "",
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const started = useRef(false);
  const attribution = useRef({});

  useEffect(() => {
    attribution.current = captureAttribution();
    track("lead_form_view", { variant, source: attribution.current.utm_source || "direct" });
  }, [variant]);

  const set = (k) => (e) => {
    if (!started.current) { started.current = true; track("lead_form_start", { variant }); }
    setF(x => ({ ...x, [k]: e.target.value }));
  };

  const toggleTool = (id) => {
    if (!started.current) { started.current = true; track("lead_form_start", { variant }); }
    setF(x => {
      const current = Array.isArray(x.business_tools) ? x.business_tools : [];
      if (id === "aucun") return { ...x, business_tools: current.includes("aucun") ? [] : ["aucun"] };
      const clean = current.filter(v => v !== "aucun");
      return { ...x, business_tools: clean.includes(id) ? clean.filter(v => v !== id) : [...clean, id] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setError(null); setState("sending");
    track("lead_form_submit", { variant, has_phone: !!f.phone, need: f.primary_need || "none" });
    try {
      const r = await fetch(`${apiUrl}/api/public/demo-lead`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, ...attribution.current, page: window.location.pathname }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.success) {
        setState("done");
        track("lead_form_success", { variant, source: attribution.current.utm_source || "direct", campaign: attribution.current.utm_campaign || "" });
      } else { setError(j.error || "Votre demande n'a pas pu être envoyée."); setState("error"); }
    } catch (err) { setError("Connexion impossible. Réessayez dans un instant."); setState("error"); }
  };

  if (state === "done") {
    const first = f.name.trim().split(/\s+/)[0];
    return (
      <div className="lv2-form" role="status">
        <div className="lv2-form-done">
          <h3>Merci{first ? ` ${first}` : ""}.</h3>
          <p>Votre demande est bien enregistrée.</p>
          <p>Nous vous contacterons rapidement pour vous présenter VIQ.AI.</p>
          <button type="button" className="lv2-btn secondary md" onClick={onDiscover}>Découvrir VehicleIQ</button>
        </div>
      </div>
    );
  }

  return (
    <form className="lv2-form" onSubmit={submit} noValidate>
      <div className="lv2-form-title">Découvrez comment VIQ.AI devient votre assistant IA proactif.</div>
      <div className="lv2-form-sub">Laissez vos coordonnées : nous vous montrons comment les 9 automatisations VIQ.AI travaillent en arrière-plan pour le sourcing, les réponses, les leads, le workflow, les appels IA, la voix et WhatsApp.</div>

      <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

      <label className="lv2-field"><span>Nom et prénom</span>
        <input required value={f.name} onChange={set("name")} autoComplete="name" placeholder="Jean Martin" /></label>
      <div className="lv2-row">
        <label className="lv2-field"><span>Entreprise</span>
          <input value={f.company} onChange={set("company")} autoComplete="organization" placeholder="Auto Prestige" /></label>
        <label className="lv2-field"><span>SIRET <em>(facultatif)</em></span>
          <input value={f.siret} onChange={set("siret")} inputMode="numeric" autoComplete="off" placeholder="123 456 789 00012" maxLength={18} /></label>
      </div>
      <label className="lv2-field"><span>Adresse</span>
        <input value={f.address} onChange={set("address")} autoComplete="street-address" placeholder="12 rue de la République, 59000 Lille" /></label>
      <div className="lv2-row">
        <label className="lv2-field"><span>Email professionnel</span>
          <input required type="email" inputMode="email" value={f.email} onChange={set("email")} autoComplete="email" placeholder="jean@autoprestige.fr" /></label>
        <label className="lv2-field"><span>Téléphone</span>
          <InternationalPhoneField
            value={f.phone}
            onChange={(phone) => setF(x => ({ ...x, phone }))}
            selectStyle={{ width: "100%" }}
            inputStyle={{ width: "100%" }}
          /></label>
      </div>
      <div className="lv2-row">
        <label className="lv2-field"><span>Taille de l'entreprise</span>
          <select value={f.company_size} onChange={set("company_size")}>
            <option value="">Choisir…</option>
            {COMPANY_SIZES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        <label className="lv2-field"><span>VO / mois</span>
          <input type="number" inputMode="numeric" min="0" value={f.vehicle_volume} onChange={set("vehicle_volume")} placeholder="20" /></label>
      </div>
      <label className="lv2-field"><span>Budget annuel estimé en outils digitaux <em>(hors IA)</em></span>
        <select value={f.annual_digital_budget} onChange={set("annual_digital_budget")}>
          <option value="">Choisir un palier…</option>
          {DIGITAL_BUDGETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></label>
      <label className="lv2-field"><span>Besoin principal</span>
        <select value={f.primary_need} onChange={set("primary_need")}>
          <option value="">Choisir…</option>
          {NEEDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></label>

      <div className="lv2-field">
        <span>Outils métier déjà utilisés</span>
        <details className="lv2-multiselect">
          <summary>
            {f.business_tools.length
              ? `${f.business_tools.length} outil${f.business_tools.length > 1 ? "s" : ""} sélectionné${f.business_tools.length > 1 ? "s" : ""}`
              : "Sélectionner un ou plusieurs outils…"}
          </summary>
          <div className="lv2-multiselect-menu">
            {BUSINESS_TOOLS.map(([id, label]) => (
              <label key={id} className="lv2-multiselect-option">
                <input type="checkbox" checked={f.business_tools.includes(id)} onChange={() => toggleTool(id)} />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </details>
      </div>

      {error && <div className="lv2-form-error" role="alert">{error}</div>}

      <button type="submit" className="lv2-btn primary lg" disabled={state === "sending"} style={{ width: "100%" }}>
        {state === "sending" ? "Envoi…" : "Confirmer sa réservation"}
      </button>
      <div className="lv2-consent">
        En envoyant ce formulaire, vous acceptez d'être recontacté par VehicleIQ.{" "}
        <button type="button" onClick={() => onPrivacy && onPrivacy("privacy")}>Politique de confidentialité</button>
      </div>
    </form>
  );
}

/* v279 legacy lead-form regression tokens — non rendus :
Laissez VIQ.AI orchestrer votre Workflow sourcing.
VIQ.AI s'intègre à votre activité sourcing VO
*/
